import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button, Table, Spin, message, Modal, Tag,
  Space, InputNumber, Select, Typography, Form, Input,
} from "antd";
import {
  EditOutlined, DeleteOutlined, DollarOutlined,
  PhoneOutlined, ShopOutlined, PrinterOutlined, EyeOutlined, RiseOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { apiClient } from "../../../shared/api/AllApi";
import { formatCurrency, formatNumber, toApiNumber } from "../../../utils/formatters";
import { fetchSystemConfig, readCachedSystemConfig, getPrintColors } from "../../../shared/lib/systemConfig";
import { fixedStart } from "../../../i18";
import {
  formatFarmerPaymentNo,
  formatSettlementNo,
  formatOrderNo,
} from "../../../shared/lib/documentNumbers";
import { fetchExchangeRates } from "../../../shared/lib/exchangeRates";
import { parseLocalizedName } from "../../../utils/localization";
import { PageHeader, StatCard, T, cardStyle } from "../../../shared/ui/PageShell";
import DateRangeFilter from "../../../shared/ui/DateRangeFilter";
import { printFarmerSettlementStatement } from "../lib/printFarmerSettlement";
import { printHtml } from "../../../shared/lib/printHtml";
import { getPrintedByInfo } from "../../../shared/lib/session";
import { fetchExpenseTypes, expenseTypeLabelMap } from "../lib/expenseTypes";
import {
  vendorFinanceFromConsignment,
  vendorItemAmount,
  vendorSaleAmount,
} from "../lib/vendorFinance";
import usePermissions from "../../../hooks/usePermissions";
import { createRequestGuard, isAbortError } from "../../../shared/lib/request";
import useTablePagination from "../../../shared/ui/useTablePagination";
const { Text } = Typography;

const PRODUCT_ALL = "__all__";

const computePaidMainEquivalent = (main, secondary, rateRow) => {
  const m = Math.max(0, toApiNumber(main, 0));
  const s = Math.max(0, toApiNumber(secondary, 0));
  if (s <= 0.000001) return Math.round(m * 100) / 100;
  const secPerMain = toApiNumber(rateRow?.main_per_secondary, 0);
  if (secPerMain <= 0) return Math.round(m * 100) / 100;
  return Math.round((m + Math.round((s / secPerMain) * 100) / 100) * 100) / 100;
};


const VendorView = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission, hasAny } = usePermissions();
  const { pagination: returnsPagination } = useTablePagination();
  const { pagination: archivesPagination } = useTablePagination();
  const { pagination: paymentsPagination } = useTablePagination();
  const canPay = hasPermission("vendors.payment");
  const canIncreaseOwed = hasPermission("maintenance.reset");
  const canCancelExpenses = hasAny(["vendors.expenses_cancel", "vendors.expenses"]);
  const canEdit = hasPermission("vendors.edit");
  const canDelete = hasPermission("vendors.delete");

  const [vendor, setVendor] = useState(null);
  const [payments, setPayments] = useState([]);
  const [consignment, setConsignment] = useState(null);
  const [loadingVendor, setLoadingVendor] = useState(true);
  const [loadingPayments, setLoadingPayments] = useState(true);
  const [loadingCons, setLoadingCons] = useState(false);
  const [dateRange, setDateRange] = useState(null);
  // Empty array = all products (default "Select all"); otherwise multi-select subset
  const [productFilter, setProductFilter] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const filtersRef = useRef({ range: null, productIds: [] });
  filtersRef.current = { range: dateRange, productIds: productFilter };
  const selectedProductIds = Array.isArray(productFilter) ? productFilter : [];
  const isAllProducts = selectedProductIds.length === 0;

  // Edit modal
  const [editOpen, setEditOpen] = useState(false);
  const [editForm] = Form.useForm();
  const [editSaving, setEditSaving] = useState(false);

  // Pay modal
  const [payOpen, setPayOpen] = useState(false);
  const [payAmount, setPayAmount] = useState(0);
  const [payAmountMain, setPayAmountMain] = useState(0);
  const [payAmountSecondary, setPayAmountSecondary] = useState(0);
  const [paySubmitting, setPaySubmitting] = useState(false);
  const [exchangeRates, setExchangeRates] = useState([]);
  const [exchangeRateId, setExchangeRateId] = useState(null);

  // Settlement archives (this farmer)
  const [archives, setArchives] = useState([]);
  const [loadingArchives, setLoadingArchives] = useState(false);
  const [printingArchiveId, setPrintingArchiveId] = useState(null);
  const [expenseTypes, setExpenseTypes] = useState([]);
  const consGuardRef = useRef(createRequestGuard());

  // Delete
  const [deleteOpen, setDeleteOpen] = useState(false);

  const [systemConfig, setSystemConfig] = useState(() => readCachedSystemConfig());
  const mainCode = systemConfig?.currency_code || "IQD";
  const secondaryCode = systemConfig?.secondary_currency_code || "";
  const useDualCurrency = Boolean(secondaryCode);

  const selectedRate = useMemo(() => {
    if (!exchangeRateId || !exchangeRates.length) return null;
    return exchangeRates.find((r) => Number(r.id) === Number(exchangeRateId)) || null;
  }, [exchangeRateId, exchangeRates]);

  const appliedPayMainTotal = useMemo(() => {
    if (!useDualCurrency) return Math.round(toApiNumber(payAmount, 0) * 100) / 100;
    return computePaidMainEquivalent(payAmountMain, payAmountSecondary, selectedRate);
  }, [payAmount, payAmountMain, payAmountSecondary, selectedRate, useDualCurrency]);

  const loadConsignment = useCallback(async (range, productIds) => {
    const { signal, isCurrent } = consGuardRef.current.next();
    setLoadingCons(true);
    try {
      const params = {};
      if (range?.[0]) params.date_from = range[0].format("YYYY-MM-DD HH:mm:ss");
      if (range?.[1]) params.date_to = range[1].format("YYYY-MM-DD HH:mm:ss");
      const ids = Array.isArray(productIds)
        ? productIds.filter((x) => x != null && x !== PRODUCT_ALL)
        : [];
      if (ids.length === 1) params.product_id = ids[0];
      else if (ids.length > 1) params.product_ids = ids;
      const res = await apiClient.get(`/pos/vendor-consignment/${id}`, { params, signal });
      if (!isCurrent()) return;
      setConsignment(res.data);
      // Merge product options so the dropdown never shrinks when filtering
      const incoming = Array.isArray(res.data?.products) ? res.data.products : [];
      if (incoming.length) {
        setProductOptions((prev) => {
          if (ids.length === 0) return incoming;
          const map = new Map((prev || []).map((p) => [Number(p.product_id), p]));
          incoming.forEach((p) => map.set(Number(p.product_id), p));
          return Array.from(map.values());
        });
      }
    } catch (e) {
      if (isAbortError(e) || !isCurrent()) return;
      setConsignment(null);
    } finally {
      if (isCurrent()) setLoadingCons(false);
    }
  }, [id]);

  const applyProductFilter = useCallback((raw) => {
    const values = Array.isArray(raw) ? raw : [];
    // "Select all" is only a menu action — never keep it as a selected tag
    if (values.includes(PRODUCT_ALL) || values.length === 0) {
      setProductFilter([]);
      loadConsignment(dateRange, []);
      return;
    }
    const next = values.filter((x) => x !== PRODUCT_ALL);
    setProductFilter(next);
    loadConsignment(dateRange, next);
  }, [dateRange, loadConsignment]);

  const loadArchives = useCallback(async () => {
    setLoadingArchives(true);
    try {
      const res = await apiClient.get(`/vendors/${id}/settlement-archives`);
      setArchives(Array.isArray(res.data?.archives) ? res.data.archives : []);
    } catch {
      setArchives([]);
    } finally {
      setLoadingArchives(false);
    }
  }, [id]);

  const loadAll = useCallback(async () => {
    setLoadingVendor(true);
    try {
      const cfg = await fetchSystemConfig();
      setSystemConfig(cfg);
      const res = await apiClient.get(`/vendors/${id}`);
      setVendor(res.data);
    } catch {
      message.error(t("loadError", "Failed to load"));
    } finally {
      setLoadingVendor(false);
    }

    apiClient.get(`/vendors/${id}/payments`)
      .then((res) => {
        const raw = res.data?.data ?? res.data;
        setPayments(Array.isArray(raw) ? raw : []);
      })
      .catch(() => setPayments([]))
      .finally(() => setLoadingPayments(false));

    loadConsignment(
      filtersRef.current.range,
      filtersRef.current.productIds
    );
    loadArchives();
  }, [id, t, loadConsignment, loadArchives]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    fetchExpenseTypes().then(setExpenseTypes).catch(() => setExpenseTypes([]));
  }, []);

  useEffect(() => {
    if (!payOpen || !useDualCurrency) return;
    let cancelled = false;
    fetchExchangeRates().then((rates) => {
      if (cancelled) return;
      setExchangeRates(rates);
      setExchangeRateId((prev) =>
        prev && rates.some((r) => Number(r.id) === Number(prev)) ? prev : rates[0]?.id ?? null
      );
    }).catch(() => { if (!cancelled) { setExchangeRates([]); setExchangeRateId(null); } });
    return () => { cancelled = true; };
  }, [payOpen, useDualCurrency]);

  const openPay = () => {
    if (!vendor) return;
    const bal = Math.round(toApiNumber(vendor.balance_main, 0) * 100) / 100;
    setPayAmount(bal);
    setPayAmountMain(bal);
    setPayAmountSecondary(0);
    setPayOpen(true);
  };

  const reprintArchive = async (archiveRow) => {
    setPrintingArchiveId(archiveRow.id);
    try {
      const res = await apiClient.get(`/vendor-settlement-archives/${archiveRow.id}`);
      const archive = res.data;
      const snapshot = archive.snapshot || {};
      await printFarmerSettlementStatement({
        consignment: snapshot,
        vendor: {
          id: archive.vendor_id || vendor?.id,
          name: archive.vendor_name || snapshot.vendor?.name || vendor?.name,
          phone: archive.vendor_phone || snapshot.vendor?.phone || vendor?.phone,
          consignment_owed: snapshot.total_debt,
        },
        systemConfig,
        language: i18n.language,
        t,
        dateFrom: archive.date_from || snapshot.date_from,
        dateTo: archive.date_to || snapshot.date_to,
        settledAt: archive.settled_at,
        archiveId: archive.id,
        expenseTypes,
        previousDebt: snapshot.previous_debt,
        totalDebt: snapshot.total_debt,
        documentNo: archive.document_no || formatSettlementNo(archive.id, archive.settled_at),
        note: archive.note || snapshot.note || snapshot.settlement_note || null,
        printedBy: getPrintedByInfo(),
      });
    } catch (e) {
      message.error(e?.response?.data?.message || t("loadError", "Failed to load"));
    } finally {
      setPrintingArchiveId(null);
    }
  };

  const cancelExpense = (expenseId) => {
    Modal.confirm({
      title: t("vendorCancelExpenseConfirm", "Cancel this expense?"),
      content: t("vendorCancelExpenseHint", "It stays on record as cancelled and is no longer counted."),
      okText: t("vendorCancelExpense", "Cancel expense"),
      okButtonProps: { danger: true },
      cancelText: t("back", "Back"),
      onOk: async () => {
        try {
          await apiClient.post(`/vendor-expenses/${expenseId}/cancel`);
          message.success(t("vendorExpenseCancelled", "Expense cancelled"));
          loadAll();
        } catch (e) {
          message.error(e?.response?.data?.message || t("vendorExpenseFailed", "Could not cancel expense"));
        }
      },
    });
  };

  const submitPayment = async () => {
    if (!vendor) return;
    const max = Math.round(toApiNumber(vendor.balance_main, 0) * 100) / 100;
    const applied = useDualCurrency
      ? computePaidMainEquivalent(payAmountMain, payAmountSecondary, selectedRate)
      : Math.round(toApiNumber(payAmount, 0) * 100) / 100;
    if (applied <= 0) { message.error(t("vendorsPaymentInvalid", "Enter a valid amount.")); return; }
    if (max > 0 && applied > max + 0.0001) { message.error(t("vendorsPaymentExceedsBalance", "Amount exceeds balance.")); return; }
    setPaySubmitting(true);
    try {
      const body = useDualCurrency
        ? { amount_main: Math.round(toApiNumber(payAmountMain, 0) * 100) / 100, amount_secondary: Math.round(toApiNumber(payAmountSecondary, 0) * 100) / 100, ...(payAmountSecondary > 0 ? { exchange_rate_id: exchangeRateId } : {}) }
        : { amount: applied };
      await apiClient.post(`/vendors/${id}/record-payment`, body);
      message.success(t("vendorsPaymentSuccess", "Payment recorded."));
      setPayOpen(false);
      loadAll();
    } catch (e) {
      message.error(e?.response?.data?.message || t("vendorsPaymentFailed", "Payment not recorded."));
    } finally {
      setPaySubmitting(false);
    }
  };

  const submitEdit = async () => {
    try {
      const values = await editForm.validateFields();
      setEditSaving(true);
      await apiClient.put(`/vendors/${id}`, values);
      message.success(t("vendorsUpdated", "Vendor updated."));
      setEditOpen(false);
      loadAll();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || t("vendorsSaveFailed", "Save failed."));
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await apiClient.delete(`/vendors/${id}`);
      message.success(t("vendorsDeleted", "Vendor deleted."));
      navigate("/vendors");
    } catch (e) {
      message.error(e?.response?.data?.message || t("vendorsDeleteFailed", "Delete failed."));
    }
  };

  // Received (from batches) vs Sold reconciliation, per product.
  const productRows = useMemo(
    () => (consignment?.products || []).map((p) => ({
      ...p,
      key: p.product_id,
      displayName: parseLocalizedName(p.name, i18n.language, `#${p.product_id}`),
    })),
    [consignment, i18n.language]
  );

  // Total cases/quantity sold, net of returns, for the active filters.
  const totalQtySold = useMemo(
    () => productRows.reduce((sum, p) => sum + (parseFloat(p.cases_sold) || 0), 0),
    [productRows]
  );

  // Total kg sold for the current date / product filters (from consignment products).
  const totalWeightSold = useMemo(
    () => productRows.reduce((sum, p) => sum + (parseFloat(p.kg_sold) || 0), 0),
    [productRows]
  );

  // Payment history respects the same date range as the settlement.
  const filteredPayments = useMemo(() => {
    if (!dateRange?.[0] || !dateRange?.[1]) return payments;
    const from = dateRange[0];
    const to = dateRange[1];
    return payments.filter((p) => {
      if (!p.created_at) return false;
      const d = dayjs(p.created_at);
      return !d.isBefore(from) && !d.isAfter(to);
    });
  }, [payments, dateRange]);

  // Single payment receipt.
  const printPayment = (p) => {
    const cfg = systemConfig || {};
    const colors = getPrintColors(cfg);
    const company = (cfg.invoice_company_name || "").trim() || cfg.site_name || "";
    const docNo = p.document_no || formatFarmerPaymentNo(p.id, p.created_at);
    const esc = (x) => String(x ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    printHtml(`<!doctype html><html><head><meta charset="utf-8"><style>
      @page{size:80mm auto;margin:0}
      body{font-family:'Segoe UI',Tahoma,Arial,sans-serif;color:${colors.primary};padding:8px 10px;width:80mm;-webkit-print-color-adjust:exact;print-color-adjust:exact}
      .r{display:flex;justify-content:space-between;padding:3px 0}
      h1{font-size:16px;text-align:center;margin:0 0 6px;color:${colors.accent}}
    </style></head><body>
      <h1>${esc(company)}</h1>
      <div style="text-align:center;font-size:13px;font-weight:900;color:${colors.accent};margin-bottom:4px">${esc(docNo)}</div>
      <div style="text-align:center;font-size:12px;color:${colors.primary};opacity:.7;border-bottom:1px dashed ${colors.primary};padding-bottom:6px;margin-bottom:6px;background:${colors.soft}">${esc(t("vendorPaymentReceipt", "Farmer Payment Receipt"))}</div>
      <div class="r"><span>${esc(t("posPurchaseVendor", "Farmer"))}</span><b>${esc(vendor.name)}</b></div>
      <div class="r"><span>${esc(t("date", "Date"))}</span><span>${p.created_at ? dayjs(p.created_at).format("YYYY-MM-DD HH:mm") : "-"}</span></div>
      ${p.note ? `<div class="r"><span>${esc(t("note", "Note"))}</span><span>${esc(p.note)}</span></div>` : ""}
      <div class="r" style="font-size:18px;font-weight:900;border-top:2px dashed ${colors.accent};margin-top:6px;padding-top:6px;color:${colors.accent}"><span>${esc(t("amountPaid", "Amount"))}</span><span>${formatCurrency(p.amount_paid, mainCode)}</span></div>
    </body></html>`);
  };

  const paymentColumns = [
    { title: "#", key: "idx", width: 46, render: (_, __, i) => <span style={{ color: T.muted }}>{i + 1}</span> },
    {
      title: <b>{t("docNo", "No.")}</b>, key: "document_no", width: 140,
      render: (_, p) => (
        <span style={{ fontWeight: 700, fontFamily: "monospace", fontSize: 12 }}>
          {p.document_no || formatFarmerPaymentNo(p.id, p.created_at)}
        </span>
      ),
    },
    {
      title: <b>{t("amountPaid", "Amount Paid")}</b>, dataIndex: "amount_paid", key: "amount_paid",
      render: (v) => {
        const n = toApiNumber(v, 0);
        // Negative = manual increase of what we owe the farmer
        return <b style={{ color: n < 0 ? T.red : T.green }}>{formatCurrency(n, mainCode)}</b>;
      },
    },
    {
      title: <b>{t("note", "Note")}</b>, dataIndex: "note", key: "note",
      render: (v) => <span style={{ color: T.sub }}>{v || "—"}</span>,
    },
    {
      title: <b>{t("date", "Date")}</b>, dataIndex: "created_at", key: "created_at",
      render: (v) => <span style={{ color: T.muted, fontSize: 12 }}>{v ? dayjs(v).format("DD/MM/YYYY HH:mm") : "—"}</span>,
    },
    {
      title: "", key: "print", width: 44,
      render: (_, p) => <Button size="small" type="text" icon={<PrinterOutlined />} onClick={() => printPayment(p)} title={t("print", "Print")} />,
    },
  ];

  // Consignment sales as a table (like the customer order history) — rows expand
  // to show the line items.
  const renderSaleItems = (sale) => {
    const items = Array.isArray(sale.items) ? sale.items : [];
    if (!items.length) return <span style={{ color: T.muted }}>—</span>;
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 4, padding: "4px 8px" }}>
        {items.map((item, ii) => {
          const name = item.product ? parseLocalizedName(item.product.name, i18n.language, `#${item.product.id}`) : t("product", "Product");
          const soldW = parseFloat(item.sold_weight) || 0;
          const soldQ = parseFloat(item.sold_qty) || parseFloat(item.sold_quantity) || 0;
          const w = parseFloat(item.weight) || 0;
          const q = parseFloat(item.qty) || parseFloat(item.quantity) || 0;
          const returned = parseFloat(item.returned) || 0;
          const returnedQty = parseFloat(item.returned_qty) || 0;
          const returnedWeight = parseFloat(item.returned_weight) || 0;
          const returnedValue = parseFloat(item.returned_value) || 0;
          const soldUnits = [
            soldQ > 0 ? `${soldQ} ${t("posQty", "pcs")}` : null,
            soldW > 0 ? `${soldW} ${t("kg", "kg")}` : null,
          ].filter(Boolean).join(" · ")
            || [
              q > 0 ? `${q} ${t("posQty", "pcs")}` : null,
              w > 0 ? `${w} ${t("kg", "kg")}` : null,
            ].filter(Boolean).join(" · ")
            || "—";
          const returnedUnits = [
            returnedQty > 0 ? `${returnedQty} ${t("posQty", "pcs")}` : null,
            returnedWeight > 0 ? `${returnedWeight} ${t("kg", "kg")}` : null,
          ].filter(Boolean).join(" · ")
            || (returned > 0
              ? `${returned}${soldW > 0 || w > 0 ? ` ${t("kg", "kg")}` : ` ${t("posQty", "pcs")}`}`
              : "");
          const netUnits = [
            q > 0 ? `${q} ${t("posQty", "pcs")}` : null,
            w > 0 ? `${w} ${t("kg", "kg")}` : null,
          ].filter(Boolean).join(" · ");
          return (
            <div key={ii} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: T.sub, gap: 12, flexWrap: "wrap" }}>
              <span style={{ fontWeight: 600, color: T.text }}>
                {name}{" "}
                <span style={{ color: T.muted, fontWeight: 400 }}>
                  · {t("vendorSold", "Sold")} {soldUnits}
                  {returnedUnits && (
                    <> · <span style={{ color: T.red }}>{t("returned", "Returned")} {returnedUnits}</span></>
                  )}
                  {returnedUnits && netUnits ? <> · {t("vendorNetRemaining", "Net")} {netUnits}</> : null}
                </span>
              </span>
              <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
                {returnedValue > 0 && (
                  <span style={{ color: T.red, fontSize: 12 }}>−{formatCurrency(returnedValue, mainCode)}</span>
                )}
                <span>{formatCurrency(vendorItemAmount(item), mainCode)}</span>
                <b style={{ color: T.text }}>{formatCurrency(vendorItemAmount(item), mainCode)}</b>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const saleColumns = [
    {
      title: <b>{t("posReceiptSale", "Sale")}</b>, key: "id", width: 90,
      render: (_, s) => (
        <Link to={`/orders/${s.id}/view`} style={{ fontWeight: 700 }}>
          {s.document_no || formatOrderNo(s.id, s.created_at)}
        </Link>
      ),
    },
    {
      title: <b>{t("customer", "Customer")}</b>, key: "customer",
      render: (_, s) => <span style={{ color: T.sub }}>{s.customer?.name || "—"}</span>,
    },
    {
      title: <b>{t("date", "Date")}</b>, key: "created_at", width: 120,
      render: (_, s) => <span style={{ color: T.muted, fontSize: 12 }}>{s.created_at ? dayjs(s.created_at).format("DD/MM/YYYY") : "—"}</span>,
    },
    {
      title: <b>{t("status", "Status")}</b>, key: "status", width: 110,
      render: (_, s) => {
        if (s.status === "returned" || (s.has_returns && !(parseFloat(s.gross_total) > 0.0001))) {
          return <Tag color="orange">{t("returned", "Returned")}</Tag>;
        }
        if (s.has_returns || parseFloat(s.returned_amount) > 0) {
          return <Tag color="gold">{t("partialReturn", "Partial return")}</Tag>;
        }
        return <Tag color="green">{t("confirmed", "Confirmed")}</Tag>;
      },
    },
    {
      title: <b>{t("vendorSold", "Sold")}</b>, key: "gross_sold", align: "right",
      render: (_, s) => <span>{formatCurrency(vendorSaleAmount(s), mainCode)}</span>,
    },
    {
      title: <b>{t("returned", "Returned")}</b>, dataIndex: "returned_amount", key: "returned_amount", align: "right",
      render: (v) => (parseFloat(v) > 0
        ? <b style={{ color: T.red }}>−{formatCurrency(v, mainCode)}</b>
        : <span style={{ color: T.muted }}>—</span>),
    },
    {
      title: <b>{t("vendorNet", "Net")}</b>, key: "net_owed", align: "right",
      render: (_, s) => <b style={{ color: T.text }}>{formatCurrency(vendorSaleAmount(s), mainCode)}</b>,
    },
    {
      title: "", key: "view", width: 56, align: "center",
      render: (_, s) => (
        <Button size="small" icon={<EyeOutlined />} style={{ borderRadius: 7, background: T.borderL, borderColor: T.border }} onClick={() => navigate(`/orders/${s.id}/view`)} />
      ),
    },
  ];

  if (loadingVendor) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 320 }}>
        <Spin size="large" />
      </div>
    );
  }
  if (!vendor) return null;

  const balance = toApiNumber(vendor.balance_main, 0);
  // Farmer payback = product totals − expenses only (commission is never deducted).
  const finance = vendorFinanceFromConsignment(consignment || {});
  const owed = finance.remaining;

  const expLabels = expenseTypeLabelMap(expenseTypes);
  const expenseLabel = (tp) => expLabels[tp] || tp;
  const sRow = (label, val, color, bold) => (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0" }}>
      <span style={{ color: T.sub, fontWeight: bold ? 700 : 400, fontSize: bold ? 15 : 14 }}>{label}</span>
      <span style={{ color: color || T.text, fontWeight: bold ? 800 : 600, fontSize: bold ? 16 : 14 }}>{val}</span>
    </div>
  );

  const headerActions = (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      {canPay && balance > 0.0001 && (
        <Button type="primary" icon={<DollarOutlined />} style={{ borderRadius: 9 }} onClick={() => openPay()}>
          {t("vendorsPay", "Pay")} {formatCurrency(balance, mainCode)}
        </Button>
      )}
      {canIncreaseOwed && (
        <Link to={`/vendors/${id}/increase-owed`}>
          <Button icon={<RiseOutlined />} style={{ borderRadius: 9 }}>
            {t("increaseFarmerOwedTitle", "Increase Amount Owed to Farmer")}
          </Button>
        </Link>
      )}
      {canEdit && (
        <Button icon={<EditOutlined />} style={{ borderRadius: 9 }} onClick={() => {
          editForm.setFieldsValue({ name: vendor.name, phone: vendor.phone || "", notes: vendor.notes || "" });
          setEditOpen(true);
        }}>
          {t("edit", "Edit")}
        </Button>
      )}
      {canDelete && (
        <Button danger icon={<DeleteOutlined />} style={{ borderRadius: 9 }} onClick={() => setDeleteOpen(true)}>
          {t("delete", "Delete")}
        </Button>
      )}
    </div>
  );

  return (
    <>
      <div style={{ padding: 24, background: T.bg, minHeight: "100%" }}>
        <PageHeader
          breadcrumb={[t("vendorsMenu", "Vendors"), vendor.name]}
          title={vendor.name}
          subtitle={vendor.phone && <span style={{ color: T.sub }}><PhoneOutlined style={{ marginInlineEnd: 4 }} />{vendor.phone}</span>}
          action={headerActions}
        />
        <div className="velan-stats-row">
          <StatCard key="products" icon={<ShopOutlined style={{ color: T.sub }} />} label={t("vendorProductsSold", "Products Sold")} value={productRows.length} iconBg={T.borderL} />
          <StatCard key="qty" icon={<ShopOutlined style={{ color: T.sub }} />} label={t("vendorNetQtySold", "Qty sold (net)")} value={formatNumber(totalQtySold)} iconBg={T.borderL} />
          <StatCard
            key="weight"
            icon={<ShopOutlined style={{ color: T.sub }} />}
            label={t("vendorWeightSold", "Weight sold")}
            value={`${formatNumber(totalWeightSold)} ${t("kg", "kg")}`}
            iconBg={T.borderL}
          />
          <StatCard key="payments" icon={<DollarOutlined style={{ color: T.sub }} />} label={t("paymentHistory", "Payment History")} value={filteredPayments.length} iconBg={T.borderL} />
          <StatCard key="balance" icon={<DollarOutlined style={{ color: owed > 0 ? T.red : T.green }} />} label={t("vendorRemainingToClear", "Owed to farmer")} value={formatCurrency(owed, mainCode)} iconBg={owed > 0 ? "#fee2e2" : "#dcfce7"} valueColor={owed > 0 ? T.red : T.green} />
        </div>
        {/* ── Account Settlement ── */}
        <div style={{ ...cardStyle, marginBottom: 16 }}>
          <div style={{ padding: "14px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
            <span style={{ fontWeight: 700, fontSize: 15, color: T.text }}>
              <DollarOutlined style={{ marginInlineEnd: 8, color: T.sub }} />
              {t("vendorSettlement", "Account Settlement")}
            </span>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <Select
                size="small"
                mode="multiple"
                allowClear
                showSearch
                optionFilterProp="label"
                maxTagCount="responsive"
                placeholder={t("vendorProductSelectAll", "Select all")}
                style={{ minWidth: 220, maxWidth: 360 }}
                value={selectedProductIds}
                onChange={applyProductFilter}
                options={[
                  { value: PRODUCT_ALL, label: t("vendorProductSelectAll", "Select all") },
                  ...productOptions.map((p) => ({
                    value: p.product_id,
                    label: parseLocalizedName(p.name, i18n.language, `#${p.product_id}`),
                  })),
                ]}
              />
              <DateRangeFilter
                size="small"
                value={dateRange}
                onChange={(v) => { setDateRange(v); loadConsignment(v, selectedProductIds); }}
                showTime
                allowClear
              />
            </div>
          </div>

          <Spin spinning={loadingCons}>
            {consignment && (
              <div
                className="vendor-settle-grid"
                style={{
                  padding: "16px 20px",
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) minmax(220px, 300px)",
                  gap: 24,
                  alignItems: "start",
                }}
              >
                {/* Breakdown */}
                <div>
                  {sRow(t("vendorSold", "Sold"), formatCurrency(finance.gross, mainCode), T.text)}
                  {(parseFloat(consignment.total_returned) > 0) && sRow(t("returned", "Returned"), `− ${formatCurrency(consignment.total_returned, mainCode)}`, "#e11d48")}
                  {sRow(t("vendorGrossSold", "Gross sold"), formatCurrency(finance.gross, mainCode), T.text)}
                  {/* Commission is never shown/deducted on farmer payback — company profit only. */}
                  {sRow(t("batchExpenses", "Expenses"), `− ${formatCurrency(finance.expenses, mainCode)}`, "#d97706")}
                  <div style={{ borderTop: `1px solid ${T.border}`, margin: "8px 0" }} />
                  {sRow(t("batchNetOwed", "Net owed"), formatCurrency(finance.netOwed, mainCode), "#0891b2", true)}
                  {sRow(t("vendorAlreadyPaid", "Already paid"), `− ${formatCurrency(finance.paid, mainCode)}`, "#16a34a")}
                </div>

                {/* Remaining */}
                <div style={{
                  background: finance.remaining > 0 ? "#fff1f2" : "#f0fdf4",
                  border: `1px solid ${finance.remaining > 0 ? "#fecaca" : "#bbf7d0"}`,
                  borderRadius: 12, padding: 18, textAlign: "center",
                }}>
                  <div style={{ fontSize: 11, color: T.muted, textTransform: "uppercase", letterSpacing: "0.05em" }}>{t("vendorRemainingToClear", "Remaining to clear")}</div>
                  <div style={{ fontSize: 28, fontWeight: 900, color: finance.remaining > 0 ? T.red : T.green, margin: "6px 0" }}>
                    {formatCurrency(finance.remaining, mainCode)}
                  </div>
                  {finance.remaining <= 0
                    && !(consignment.sales?.length > 0)
                    && !(consignment.products?.length > 0)
                    && !((consignment.expenses || []).some((e) => !e.cancelled_at && !e.settlement_archive_id)) && (
                      <div style={{ color: T.green, fontWeight: 700, marginTop: 8 }}>✓ {t("vendorAccountCleared", "Account cleared")}</div>
                  )}
                </div>
              </div>
            )}
          </Spin>
        </div>

        {/* ── Expenses (table) ── */}
        <div style={{ ...cardStyle, marginBottom: 16 }}>
          <div style={{ padding: "14px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
            <span style={{ fontWeight: 700, fontSize: 15, color: T.text }}>
              <DollarOutlined style={{ marginInlineEnd: 8, color: T.sub }} />
              {t("batchExpenses", "Expenses")}
            </span>
          </div>
          <Table
            size="small"
            loading={loadingCons}
            dataSource={(consignment?.expenses || []).map((e, i) => ({ ...e, key: e.id ?? i }))}
            pagination={false}
            scroll={{ x: 560 }}
            locale={{ emptyText: t("vendorNoExpenses", "No expenses yet") }}
            columns={[
              {
                title: "", key: "actions", width: 150, align: "center", fixed: fixedStart(i18n.language),
                render: (_, e) => {
                  if (e.cancelled_at) {
                    return <span style={{ fontSize: 11, color: T.red }}>{t("vendorExpenseCancelledBy", "Cancelled")}{e.cancelled_by_name ? ` · ${e.cancelled_by_name}` : ""}</span>;
                  }
                  if (e.settlement_archive_id) {
                    return <Tag color="purple">{t("archived", "Archived")}</Tag>;
                  }
                  if (!canCancelExpenses) return null;
                  return <Button size="small" danger icon={<DeleteOutlined />} style={{ borderRadius: 7 }} onClick={() => cancelExpense(e.id)}>{t("cancel", "Cancel")}</Button>;
                },
              },
              {
                title: <b>{t("type", "Type")}</b>, dataIndex: "type", key: "type",
                render: (v, e) => (
                  <Tag color={e.cancelled_at ? "default" : e.settlement_archive_id ? "purple" : "orange"}>
                    {expenseLabel(v)}
                  </Tag>
                ),
              },
              {
                title: <b>{t("product", "Product")}</b>, key: "product",
                render: (_, e) => e.product_id
                  ? <span style={{ fontWeight: 600 }}>{parseLocalizedName(e.product_name, i18n.language, `#${e.product_id}`)}</span>
                  : <span style={{ color: T.muted }}>{t("vendorExpenseFullAccount", "Full account")}</span>,
              },
              {
                title: <b>{t("amountPaid", "Amount")}</b>, dataIndex: "amount", key: "amount", align: "right",
                render: (v, e) => e.cancelled_at
                  ? <span style={{ color: T.muted, textDecoration: "line-through" }}>−{formatCurrency(v, mainCode)}</span>
                  : <b style={{ color: T.red }}>−{formatCurrency(v, mainCode)}</b>,
              },
              {
                title: <b>{t("note", "Note")}</b>, dataIndex: "note", key: "note",
                render: (v) => <span style={{ color: T.sub }}>{v || "—"}</span>,
              },
              {
                title: <b>{t("date", "Date")}</b>, dataIndex: "created_at", key: "created_at", width: 140,
                render: (v) => <span style={{ color: T.muted, fontSize: 12 }}>{v ? dayjs(v).format("DD/MM/YYYY HH:mm") : "—"}</span>,
              },
            ]}
          />
        </div>

        {/* ── Consignment Sales (temporarily hidden) ──
        <div style={{ ...cardStyle, marginBottom: 16 }}>
          <div style={{ padding: "14px 20px", borderBottom: `1px solid ${T.border}` }}>
            <span style={{ fontWeight: 700, fontSize: 15, color: T.text }}>
              <ShopOutlined style={{ marginInlineEnd: 8, color: "#0891b2" }} />
              {t("vendorConsignmentSales", "Consignment Sales")}
            </span>
          </div>

          <Table
            size="small"
            loading={loadingCons}
            dataSource={(consignment?.sales || []).map((s) => ({ ...s, key: s.id }))}
            columns={saleColumns}
            expandable={{
              expandedRowRender: renderSaleItems,
              rowExpandable: (r) => Array.isArray(r.items) && r.items.length > 0,
            }}
            pagination={{ pageSize: 10, size: "small" }}
            scroll={{ x: 640 }}
            locale={{ emptyText: t("vendorNoConsignment", "No consignment sales yet") }}
          />
        </div>
        */}

        {/* ── Returns (explicit list — which order was returned) ── */}
        {(consignment?.returns || []).length > 0 && (
          <div style={{ ...cardStyle, marginBottom: 16 }}>
            <div style={{ padding: "14px 20px", borderBottom: `1px solid ${T.border}` }}>
              <span style={{ fontWeight: 700, fontSize: 15, color: T.text }}>{t("returns", "Returns")}</span>
              <span style={{ fontSize: 12, color: T.muted, marginInlineStart: 8 }}>
                {t("vendorReturnsHint", "Orders returned — amounts deducted from farmer balance")}
              </span>
            </div>
            <Table
              size="small"
              dataSource={(consignment.returns || []).map((r, i) => ({ ...r, key: r.id ?? i }))}
              pagination={{ ...returnsPagination, size: "small" }}
              scroll={{ x: 640 }}
              columns={[
                {
                  title: <b>{t("posReceiptSale", "Sale")}</b>, key: "order",
                  render: (_, r) => (
                    <Link to={`/orders/${r.order_id}/view`} style={{ fontWeight: 700 }}>
                      {r.document_no || formatOrderNo(r.order_id, r.order_created_at)}
                    </Link>
                  ),
                },
                {
                  title: <b>{t("product", "Product")}</b>, key: "product",
                  render: (_, r) => (
                    <span style={{ fontWeight: 600 }}>
                      {r.product ? parseLocalizedName(r.product.name, i18n.language, `#${r.product.id}`) : "—"}
                    </span>
                  ),
                },
                {
                  title: <b>{t("quantity", "Qty")}</b>, dataIndex: "return_qty", key: "return_qty", align: "center", width: 80,
                  render: (v) => (Number(v) > 0 ? <Tag color="orange">{v}</Tag> : <span style={{ color: T.muted }}>—</span>),
                },
                {
                  title: <b>{t("weight", "Weight")}</b>, dataIndex: "return_weight", key: "return_weight", align: "center", width: 90,
                  render: (v) => (Number(v) > 0 ? <Tag color="blue">{v} {t("kg", "kg")}</Tag> : <span style={{ color: T.muted }}>—</span>),
                },
                {
                  title: <b>{t("returned", "Returned")}</b>, dataIndex: "refund_amount", key: "refund_amount", align: "right",
                  render: (v) => <b style={{ color: "#e11d48" }}>−{formatCurrency(v, mainCode)}</b>,
                },
                {
                  title: <b>{t("date", "Date")}</b>, dataIndex: "created_at", key: "created_at", width: 120,
                  render: (v) => <span style={{ color: T.muted, fontSize: 12 }}>{v ? dayjs(v).format("DD/MM/YYYY") : "—"}</span>,
                },
              ]}
            />
          </div>
        )}

        {/* ── Per-item settlement (money) (temporarily hidden) ──
        <div style={{ ...cardStyle, marginBottom: 16 }}>
          <div style={{ padding: "14px 20px", borderBottom: `1px solid ${T.border}` }}>
            <span style={{ fontWeight: 700, fontSize: 15, color: T.text }}>{t("vendorPerItemTitle", "Settlement per item")}</span>
            <span style={{ fontSize: 12, color: T.muted, marginInlineStart: 8 }}>{t("vendorPerItemHint", "what sold of each item (cases and kg) and what it clears")}</span>
          </div>
          <Table
            size="small"
            loading={loadingCons}
            dataSource={productRows}
            pagination={false}
            scroll={{ x: 640 }}
            locale={{ emptyText: t("noDataFound", "No data found") }}
            columns={[
              {
                title: <b>{t("product", "Product")}</b>, dataIndex: "displayName", key: "name",
                render: (v) => <span style={{ fontWeight: 600 }}>{v}</span>,
              },
              {
                title: <b>{t("posReceiptPieces", "Cases / Qty")}</b>, key: "cases_sold", align: "center", width: 130,
                render: (_, r) => {
                  const net = parseFloat(r.cases_sold) || 0;
                  const ret = parseFloat(r.cases_returned) || 0;
                  const sold = parseFloat(r.cases_sold_gross) || (net + ret);
                  if (sold <= 0 && net <= 0) return <span style={{ color: T.muted }}>—</span>;
                  return (
                    <span>
                      {ret > 0 ? (
                        <>
                          <Tag color="geekblue">{sold}</Tag>
                          <Tag color="orange">−{ret}</Tag>
                          <Tag color="green">={net}</Tag>
                        </>
                      ) : (
                        <Tag color="geekblue">{net}</Tag>
                      )}
                    </span>
                  );
                },
              },
              {
                title: <b>{t("weight", "Kg")}</b>, key: "kg_sold", align: "center", width: 140,
                render: (_, r) => {
                  const net = parseFloat(r.kg_sold) || 0;
                  const ret = parseFloat(r.kg_returned) || 0;
                  const sold = parseFloat(r.kg_sold_gross) || (net + ret);
                  if (sold <= 0 && net <= 0) return <span style={{ color: T.muted }}>—</span>;
                  return (
                    <span>
                      {ret > 0 ? (
                        <>
                          <Tag color="green">{sold} {t("kg", "kg")}</Tag>
                          <Tag color="orange">−{ret}</Tag>
                          <Tag>={net}</Tag>
                        </>
                      ) : (
                        <Tag color="green">{net} {t("kg", "kg")}</Tag>
                      )}
                    </span>
                  );
                },
              },
              {
                title: <b>{t("vendorSold", "Sold")}</b>, key: "gross_sold", align: "right",
                render: (_, r) => <span>{formatCurrency(r.gross_sold != null ? r.gross_sold : r.gross, mainCode)}</span>,
              },
              {
                title: <b>{t("returned", "Returned")}</b>, dataIndex: "returned_value", key: "returned_value", align: "right", width: 110,
                render: (v) => (parseFloat(v) > 0
                  ? <b style={{ color: "#e11d48" }}>−{formatCurrency(v, mainCode)}</b>
                  : <span style={{ color: T.muted }}>—</span>),
              },
              {
                title: <b>{t("vendorGross", "Gross")}</b>, dataIndex: "gross", key: "gross", align: "right",
                render: (v) => <span>{formatCurrency(v, mainCode)}</span>,
              },
              // Commission is intentionally hidden from the farmer view.
              {
                title: <b>{t("batchExpenses", "Expenses")}</b>, dataIndex: "expenses", key: "expenses", align: "right", width: 120,
                render: (v) => (v > 0
                  ? <b style={{ color: "#d97706" }}>−{formatCurrency(v, mainCode)}</b>
                  : <span style={{ color: T.muted }}>—</span>),
              },
              {
                title: <b>{t("vendorNet", "Net")}</b>, dataIndex: "net", key: "net", align: "right",
                render: (v) => <b style={{ color: "#0891b2" }}>{formatCurrency(v, mainCode)}</b>,
              },
            ]}
          />
        </div>
        */}

        {/* ── Settlement Archives ── */}
        <div style={{ ...cardStyle, marginBottom: 16 }}>
          <div style={{ padding: "14px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
            <span style={{ fontWeight: 700, fontSize: 15, color: T.text }}>
              {t("farmerArchivesTitle", "Settlement Archives")}
            </span>
            <Link to="/farmer-settlement-archives" style={{ fontSize: 13 }}>
              {t("vendorViewAllArchives", "View all archives")}
            </Link>
          </div>
          <Table
            size="small"
            loading={loadingArchives}
            dataSource={archives.map((a) => ({ ...a, key: a.id }))}
            pagination={{ ...archivesPagination, size: "small" }}
            scroll={{ x: 640 }}
            locale={{ emptyText: t("vendorNoArchives", "No cleared statements yet") }}
            columns={[
              {
                title: <b>{t("docNo", "No.")}</b>, key: "document_no", width: 140,
                render: (_, r) => (
                  <span style={{ fontWeight: 700, fontFamily: "monospace", fontSize: 12 }}>
                    {r.document_no || formatSettlementNo(r.id, r.settled_at)}
                  </span>
                ),
              },
              {
                title: <b>{t("vendorArchivePeriod", "Period")}</b>, key: "period",
                render: (_, r) => <span style={{ fontSize: 12 }}>{(r.date_from || "—") + " → " + (r.date_to || "—")}</span>,
              },
              {
                title: <b>{t("vendorGross", "Gross")}</b>, dataIndex: "gross", key: "gross", align: "right",
                render: (v) => formatCurrency(v, mainCode),
              },
              {
                title: <b>{t("vendorNet", "Net")}</b>, dataIndex: "net", key: "net", align: "right",
                render: (v) => <b style={{ color: "#0891b2" }}>{formatCurrency(v, mainCode)}</b>,
              },
              {
                title: <b>{t("date", "Date")}</b>, dataIndex: "settled_at", key: "settled_at", width: 120,
                render: (v) => <span style={{ fontSize: 12, color: T.muted }}>{v ? dayjs(v).format("DD/MM/YYYY") : "—"}</span>,
              },
              {
                title: <b>{t("clearedBy", "Cleared by")}</b>, dataIndex: "created_by_name", key: "cleared_by", width: 120,
                render: (v) => <span style={{ fontSize: 12, fontWeight: 600 }}>{v || "—"}</span>,
              },
              {
                title: "", key: "print", width: 100, align: "center",
                render: (_, r) => (
                  <Button size="small" icon={<PrinterOutlined />} loading={printingArchiveId === r.id} style={{ borderRadius: 7 }} onClick={() => reprintArchive(r)}>
                    {t("print", "Print")}
                  </Button>
                ),
              },
            ]}
          />
        </div>

        {/* ── Payment History ── */}
        <div style={cardStyle}>
          <div style={{ padding: "14px 20px", borderBottom: `1px solid ${T.border}` }}>
            <span style={{ fontWeight: 700, fontSize: 15, color: T.text }}>{t("paymentHistory", "Payment History")}</span>
          </div>
          <Table
            size="small" loading={loadingPayments}
            dataSource={filteredPayments.map((p, i) => ({ ...p, key: p.id ?? i }))}
            columns={paymentColumns}
            pagination={{ ...paymentsPagination, size: "small" }}
            scroll={{ x: 400 }}
            locale={{ emptyText: t("noDataFound", "No data found") }}
          />
        </div>
      </div>

      {/* Edit modal */}
      <Modal title={t("vendorsEditTitle", "Edit Vendor")} open={editOpen} onCancel={() => setEditOpen(false)}
        onOk={submitEdit} confirmLoading={editSaving} okText={t("purchaseOrdersSave", "Save")} destroyOnHidden>
        <Form form={editForm} layout="vertical">
          <Form.Item name="name" label={t("purchaseOrdersVendorName", "Name")} rules={[{ required: true, message: t("vendorNameRequired", "Vendor name is required") }]}>
            <Input />
          </Form.Item>
          <Form.Item name="phone" label={t("purchaseOrdersVendorPhone", "Phone")}>
            <Input />
          </Form.Item>
          <Form.Item name="notes" label={t("vendorsNotes", "Notes")}>
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Pay modal */}
      <Modal title={t("vendorsPayModalTitle", "Pay Vendor")} open={payOpen}
        onCancel={() => setPayOpen(false)} onOk={submitPayment}
        confirmLoading={paySubmitting} okText={t("vendorsPaySubmit", "Confirm Payment")} destroyOnHidden
        width={useDualCurrency ? 440 : 400}>
        {vendor && (
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            <Text>
              {vendor.name} — {t("vendorsOutstanding", "Outstanding Balance")}: <strong>{formatCurrency(vendor.balance_main, mainCode)}</strong>
            </Text>
            {useDualCurrency ? (
              <>
                <div>
                  <Text type="secondary" style={{ display: "block", marginBottom: 4 }}>{t("poAmountPaidMain", "Amount Paid ({{code}})", { code: mainCode })}</Text>
                  <InputNumber min={0} style={{ width: "100%" }} value={payAmountMain} onChange={(v) => setPayAmountMain(v ?? 0)} />
                </div>
                <div>
                  <Text type="secondary" style={{ display: "block", marginBottom: 4 }}>{t("poAmountPaidSecondary", "Amount Paid in secondary currency ({{code}})", { code: secondaryCode })}</Text>
                  <InputNumber min={0} style={{ width: "100%" }} value={payAmountSecondary} onChange={(v) => setPayAmountSecondary(v ?? 0)} />
                </div>
                {exchangeRates.length > 0 && (
                  <div>
                    <Text type="secondary" style={{ display: "block", marginBottom: 4 }}>{t("exchangeRate", "Exchange rate")}</Text>
                    <Select style={{ width: "100%" }} value={exchangeRateId} onChange={setExchangeRateId}
                      options={exchangeRates.map((r) => ({
                        value: r.id,
                        label: `${String(r.effective_from || "").slice(0, 10)} — 1 ${mainCode} = ${r.main_per_secondary} ${secondaryCode}`,
                      }))}
                    />
                  </div>
                )}
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t("paymentTotalApplied", "Total applied")} ({mainCode}): <strong>{formatCurrency(appliedPayMainTotal, mainCode)}</strong>
                </Text>
                <Button type="link" size="small" onClick={() => { setPayAmountMain(Math.round(toApiNumber(vendor.balance_main, 0) * 100) / 100); setPayAmountSecondary(0); }}>
                  {t("vendorsPayFullBalance", "Pay full balance")}
                </Button>
              </>
            ) : (
              <>
                <div>
                  <Text type="secondary">{t("vendorsPaymentAmount", "Amount")}</Text>
                  <InputNumber min={0.01} style={{ width: "100%", marginTop: 8 }} value={payAmount} onChange={(v) => setPayAmount(v ?? 0)} />
                </div>
                <Button type="link" size="small" onClick={() => setPayAmount(Math.round(toApiNumber(vendor.balance_main, 0) * 100) / 100)}>
                  {t("vendorsPayFullBalance", "Pay full balance")}
                </Button>
              </>
            )}
          </Space>
        )}
      </Modal>

      {/* Delete confirm */}
      <Modal title={t("vendorsDeleteConfirm", "Delete this vendor? Only if no purchase orders exist.")} open={deleteOpen} onOk={handleDelete}
        onCancel={() => setDeleteOpen(false)} okType="danger" okText={t("yesDelete", "Yes, Delete")} cancelText={t("cancel", "Cancel")} centered>
        <p>{t("deleteConfirmation", "Are you sure you want to delete this customer?")}</p>
      </Modal>
    </>
  );
};

export default VendorView;
